import { InstrumentPrices } from '@/common/types'

export type SecurityUnit = { securityCode: string; units: number; priceInfo?: InstrumentPrices }
export type EquityData = { lastPrice?: number; closingPrice?: number; buyPrice?: number; pointsChange?: number }

export function aggregateCurrent(securityList: SecurityUnit[], equityData: EquityData[]) {
    // only aggregate when all results are present
    if (equityData.some((d) => d == null)) {
        return {
            aggregatedPrice: undefined,
            aggregatedPointsChange: undefined,
            aggregatedPercentageChange: undefined,
        }
    }

    let priceSum = 0
    let pointsSum = 0
    let anyData = false

    securityList.forEach((_, idx) => {
        const data = equityData[idx]
        const units = securityList[idx].units
        if (!data) return
        const current = data.lastPrice ?? data.closingPrice ?? data.buyPrice
        if (typeof current === 'number') {
            priceSum += current * units
            anyData = true
        }
        if (typeof data.pointsChange === 'number') {
            pointsSum += data.pointsChange * units
            anyData = true
        }
    })

    const aggPrice = anyData ? priceSum : undefined
    const aggPoints = anyData ? pointsSum : undefined

    let aggPerc: number | undefined = undefined
    if (aggPoints !== undefined && aggPrice !== undefined) {
        const prevTotal = aggPrice - aggPoints
        if (prevTotal && prevTotal !== 0) {
            aggPerc = (aggPoints / prevTotal) * 100
        }
    }

    return {
        aggregatedPrice: aggPrice,
        aggregatedPointsChange: aggPoints,
        aggregatedPercentageChange: aggPerc,
    }
}

export function aggregateHistorical(securityList: SecurityUnit[], historicalResults: any[]) {
    if (historicalResults.some((res) => !res.data || res.data?.length === 0)) {
        return []
    }

    // Clone data arrays so we can mutate safely
    const resultsClone = historicalResults.map((res) => ({ data: res.data ? [...res.data] : [] }))

    // Collect every date across all series and sort ascending
    const allDates = resultsClone.reduce((acc: string[], res) => {
        const dates = res.data?.map((p: any) => p.date) ?? []
        return acc.concat(dates)
    }, [])
    const uniqueSortedDates = Array.from(new Set(allDates)).sort()
    type AggregateResult = { date: string; close: number }
    const results: AggregateResult[] = []

    // previousValue holds the last known close for each security while iterating dates
    const previousValue = resultsClone.map((result) => result.data[0]?.close ?? 0)

    // Capture each series' latest entry up-front (may have different timestamps)
    const latestEntries = historicalResults.map((res) =>
        res.data && res.data.length > 0 ? res.data[res.data.length - 1] : undefined,
    )

    // Determine a robust finalDate: prefer the latest timestamp across series' latest entries,
    // otherwise fall back to the last available historical date.
    const latestDates = latestEntries.map((e) => e?.date).filter(Boolean) as string[]
    let finalDate = latestDates.length > 0 ? latestDates.sort().pop() : undefined
    if (!finalDate && uniqueSortedDates.length > 0) {
        finalDate = uniqueSortedDates[uniqueSortedDates.length - 1]
    }

    if (!finalDate) {
        // No valid dates to build a series from
        return []
    }

    // Exclude any per-series latest timestamps (they are "live" snapshots)
    const latestDatesSet = new Set(latestDates)
    const filteredDates = uniqueSortedDates.filter((d) => new Date(d) < new Date(finalDate!) && !latestDatesSet.has(d))

    // Iterate each filtered (historical) date in order and compute aggregated close using latest available value per security
    for (const date of filteredDates) {
        let sum = 0
        for (let i = 0; i < securityList.length; i++) {
            // If this series has a data point at this date, consume it and update previous value
            if (resultsClone[i].data?.[0]?.date === date) {
                const entry = resultsClone[i].data.shift()
                previousValue[i] = entry?.close ?? previousValue[i]
            }
            sum += (previousValue[i] ?? 0) * securityList[i].units
        }
        results.push({ date, close: parseFloat(sum.toFixed(2)) })
    }
    // Compute a final "latest" combined snapshot using each series' latest entry (or last known previousValue)
    const finalSum = latestEntries.reduce((acc, entry, idx) => {
        const lastClose = entry?.close ?? previousValue[idx] ?? 0
        return acc + lastClose * securityList[idx].units
    }, 0)
    const finalClose = parseFloat(finalSum.toFixed(2))

    // Append the single final snapshot at the overall finalDate
    if (results.length > 0 && results[results.length - 1].date === finalDate) {
        results[results.length - 1].close = finalClose
    } else {
        results.push({ date: finalDate, close: finalClose })
    }

    return results
}
